import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import NextEventsCard from "./NextEventsCard";
import AppContainer from "../../../AppContainer";
import EventCardSkeleton from "../../EventCardSkeleton";
import { getEventDate, getUpcomingEvents } from "../../../services/eventsUtils";

import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  makeStyles,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
} from "@material-ui/core";

import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
  },

  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
  },

  filtersTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },

  filterIcon: {
    marginRight: theme.spacing(1),
  },

  formControl: {
    width: "100%",
  },

  searchField: {
    width: "100%",
  },

  clearButton: {
    width: "100%",
    height: 56,
  },

  resultsInfo: {
    marginBottom: theme.spacing(2),
    color: "#777",
  },

  emptyContainer: {
    width: "100%",
    textAlign: "center",
    padding: theme.spacing(5, 2),
  },

  "@media (max-width: 600px)": {
    filtersContainer: {
      padding: theme.spacing(1.5),
    },

    title: {
      fontSize: "2rem",
    },
  },
}));

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const normalizeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9\s/.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getSearchableDateText = (fechaEvento) => {
  if (!fechaEvento) return "";

  const fecha = getEventDate(fechaEvento);

  if (!fecha) {
    return String(fechaEvento);
  }

  const dia = fecha.getDate();
  const mes = fecha.getMonth();
  const anio = fecha.getFullYear();

  const dia2 = String(dia).padStart(2, "0");
  const mes2 = String(mes + 1).padStart(2, "0");

  return [
    fechaEvento,
    `${anio}-${mes2}-${dia2}`,
    `${dia2}/${mes2}/${anio}`,
    `${dia}/${mes + 1}/${anio}`,
    `${dia} ${MESES[mes]} ${anio}`,
    `${MESES[mes]} ${anio}`,
    MESES[mes],
    anio,
  ].join(" ");
};

const NextEventsList = () => {
  const classes = useStyles();

  const eventsFirebase = useSelector(
    (state) => state.events.events
  );
  const cargandoEventos = eventsFirebase === undefined;

  const [busqueda, setBusqueda] = useState("");
  const [mes, setMes] = useState("");
  const [anio, setAnio] = useState("");
  const [ubicacion, setUbicacion] = useState([]);

  const events = useMemo(
    () => getUpcomingEvents(eventsFirebase),
    [eventsFirebase]
  );

  const ubicacionesDisponibles = useMemo(() => {
    const values = events
      .map((evento) => evento.ciudadEvento)
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", {
        sensitivity: "base",
      })
    );
  }, [events]);

  const aniosDisponibles = useMemo(() => {
    const values = events
      .map((evento) => {
        const fecha = getEventDate(evento.fechaEvento);

        if (!fecha) return null;

        return fecha.getFullYear();
      })
      .filter(Boolean);

    return [...new Set(values)].sort(
      (a, b) => a - b
    );
  }, [events]);

  const mesesDisponibles = useMemo(() => {
    const values = events
      .filter((evento) => {
        if (!anio) return true;

        const fecha = getEventDate(evento.fechaEvento);

        if (!fecha) return false;

        return fecha.getFullYear() === Number(anio);
      })
      .map((evento) => {
        const fecha = getEventDate(evento.fechaEvento);

        if (!fecha) return null;

        return fecha.getMonth();
      })
      .filter((value) => value !== null);

    return [...new Set(values)].sort(
      (a, b) => a - b
    );
  }, [events, anio]);

  const filteredEvents = useMemo(() => {
    const textoBusqueda = normalizeText(busqueda);

    return events.filter((evento) => {
      const fecha = getEventDate(evento.fechaEvento);

      if (textoBusqueda) {
        const nombreEvento =
          evento.nombreEvento ||
          evento.nombre ||
          evento.eventoNombre ||
          "";

        const searchableText = normalizeText(
          [
            nombreEvento,
            evento.ciudadEvento || "",
            evento.tipoDeEvento || "",
            getSearchableDateText(evento.fechaEvento),
          ].join(" ")
        );

        const palabrasBusqueda =
          textoBusqueda.split(" ").filter(Boolean);

        const coincideBusqueda =
          palabrasBusqueda.every((palabra) =>
            searchableText.includes(palabra)
          );

        if (!coincideBusqueda) {
          return false;
        }
      }

      if (ubicacion.length > 0) {
        const ciudadEvento = String(
          evento.ciudadEvento || ""
        ).trim();

        if (!ubicacion.includes(ciudadEvento)) {
          return false;
        }
      }

      if (anio) {
        if (!fecha) return false;

        if (
          fecha.getFullYear() !== Number(anio)
        ) {
          return false;
        }
      }

      if (mes !== "") {
        if (!fecha) return false;

        if (
          fecha.getMonth() !== Number(mes)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    events,
    busqueda,
    ubicacion,
    anio,
    mes,
  ]);

  const clearFilters = () => {
    setBusqueda("");
    setMes("");
    setAnio("");
    setUbicacion([]);
  };

  const hasFilters =
    busqueda.trim() !== "" ||
    mes !== "" ||
    anio ||
    ubicacion.length > 0;

  return (
    <AppContainer>
      <Typography
        color="primary"
        variant="h2"
        component="h2"
        className={classes.title}
      >
        Próximos Eventos
      </Typography>

      <Paper
        elevation={1}
        className={classes.filtersContainer}
      >
        <Typography
          variant="h6"
          className={classes.filtersTitle}
        >
          <FilterListIcon
            color="primary"
            className={classes.filterIcon}
          />

          Buscar y filtrar eventos
        </Typography>

        <Grid container spacing={2}>
          {/* BUSCADOR */}

          <Grid item xs={12} md={5}>
            <TextField
              variant="outlined"
              label="Buscar evento"
              placeholder="Nombre, ciudad, fecha o tipo..."
              value={busqueda}
              onChange={(e) =>
                setBusqueda(e.target.value)
              }
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* AÑO */}

          <Grid item xs={6} sm={3} md={2}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
            >
              <InputLabel>Año</InputLabel>

              <Select
                value={anio}
                onChange={(e) => {
                  setAnio(e.target.value);
                  setMes("");
                }}
                label="Año"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>

                {aniosDisponibles.map((year) => (
                  <MenuItem
                    key={year}
                    value={year}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* MES */}

          <Grid item xs={6} sm={3} md={2}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
            >
              <InputLabel>Mes</InputLabel>

              <Select
                value={mes}
                onChange={(e) =>
                  setMes(e.target.value)
                }
                label="Mes"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>

                {mesesDisponibles.map(
                  (monthNumber) => (
                    <MenuItem
                      key={monthNumber}
                      value={monthNumber}
                    >
                      {MESES[monthNumber]}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* UBICACIÓN */}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
            >
              <InputLabel>
                Ubicación
              </InputLabel>

              <Select
                multiple
                value={ubicacion}
                onChange={(e) =>
                  setUbicacion(e.target.value)
                }
                label="Ubicación"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "Todas";
                  }

                  if (selected.length === 1) {
                    return selected[0];
                  }

                  if (selected.length === 2) {
                    return selected.join(", ");
                  }

                  return `${selected.length} ciudades`;
                }}
              >
                {ubicacionesDisponibles.map(
                  (ciudad) => (
                    <MenuItem
                      key={ciudad}
                      value={ciudad}
                    >
                      <Checkbox
                        color="primary"
                        checked={
                          ubicacion.indexOf(ciudad) > -1
                        }
                      />

                      <ListItemText
                        primary={ciudad}
                      />
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* LIMPIAR */}

          {hasFilters && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
              >
                Limpiar filtros
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Typography
        variant="body2"
        className={classes.resultsInfo}
      >
        {cargandoEventos
          ? "Cargando eventos..."
          : filteredEvents.length === 1
          ? "1 evento encontrado"
          : `${filteredEvents.length} eventos encontrados`}
      </Typography>

      <Grid
        container
        direction="row"
        justify="flex-start"
        alignItems="flex-start"
        spacing={2}
      >
        {cargandoEventos
          ? [1, 2, 3, 4, 5, 6].map((i) => <EventCardSkeleton key={i} />)
          : filteredEvents.map((evento) => (
              <NextEventsCard
                key={
                  evento.iDEvento ||
                  evento.firebaseKey
                }
                evento={evento}
              />
            ))}

        {!cargandoEventos && filteredEvents.length === 0 && (
          <Grid item xs={12}>
            <div
              className={classes.emptyContainer}
            >
              <Typography
                variant="h6"
                color="textSecondary"
              >
                No encontramos eventos con estos criterios
              </Typography>

              {hasFilters && (
                <Button
                  color="primary"
                  onClick={clearFilters}
                  style={{ marginTop: 12 }}
                >
                  Mostrar todos los eventos
                </Button>
              )}
            </div>
          </Grid>
        )}
      </Grid>
    </AppContainer>
  );
};

export default NextEventsList;